import { useCallback, useEffect, useState } from 'react';
import { QrCode, RefreshCw } from 'lucide-react';
import {
  canUseNativeKioskEnrollment,
  enrollNativeKiosk,
  enrollNativeKioskFromManagedConfiguration,
  tokenFromEnrollmentUrl,
} from '@/lib/nativeKioskEnrollment';
import {
  isNativeScanCancellation,
  scanNativeBarcode,
} from '@/lib/nativeBarcodeScanner';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import logoUrl from '@/assets/logo.png';
import logoDarkUrl from '@/assets/logo-dark.png';

export function TabletEnrollment() {
  useDocumentTitle(['Tablet setup']);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nativeEnrollmentAvailable = canUseNativeKioskEnrollment();
  const [managedCheck, setManagedCheck] = useState<'checking' | 'missing' | 'failed'>(
    nativeEnrollmentAvailable ? 'checking' : 'missing',
  );

  const checkManagedEnrollment = useCallback(async () => {
    if (!nativeEnrollmentAvailable) return;
    setManagedCheck('checking');
    setError(null);
    try {
      const result = await enrollNativeKioskFromManagedConfiguration();
      if (result.status === 'started') return;
      if (result.mode === 'employee') {
        window.location.assign('/signin?return=%2F');
        return;
      }
      if (result.status === 'missing') {
        setManagedCheck('missing');
        return;
      }
      setManagedCheck('failed');
      setError(
        result.status === 'already_attempted'
          ? 'Automatic setup did not complete. Ask IT to issue a fresh managed enrollment token, then retry.'
          : 'The managed enrollment token is invalid. Ask IT to replace it in Scalefusion.',
      );
    } catch (managedError) {
      const message = managedError instanceof Error ? managedError.message : String(managedError);
      if (/not implemented|does not have an implementation/i.test(message)) {
        setManagedCheck('missing');
        return;
      }
      console.error('[sweetspot-kiosk-enrollment] Managed enrollment failed', managedError);
      setManagedCheck('failed');
      setError('Automatic tablet setup failed. Retry after IT confirms the Scalefusion configuration.');
    }
  }, [nativeEnrollmentAvailable]);

  useEffect(() => {
    void checkManagedEnrollment();
  }, [checkManagedEnrollment]);

  async function scanEnrollmentCode() {
    setError(null);
    setScanning(true);
    try {
      const value = await scanNativeBarcode();
      if (!value) return;

      const token = tokenFromEnrollmentUrl(value);
      if (!token) {
        setError('That is not a valid Cockpit tablet-enrollment QR code.');
        return;
      }

      await enrollNativeKiosk(token);
    } catch (scanError) {
      if (!isNativeScanCancellation(scanError)) {
        console.error('[sweetspot-kiosk-enrollment] Enrollment scan failed', scanError);
        setError('Tablet enrollment failed. Generate a fresh code in Cockpit and try again.');
      }
    } finally {
      setScanning(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-candy-cream px-4 dark:bg-candy-dark-bg">
      <section className="w-full max-w-md rounded-lg border border-candy-line bg-candy-white p-8 text-center shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
        <span className="mx-auto mb-4 flex w-fit items-center">
          <img src={logoUrl} alt="The Sweet Spot" className="h-24 w-auto dark:hidden" />
          <img src={logoDarkUrl} alt="" aria-hidden className="hidden h-20 w-auto dark:block" />
        </span>
        <h1 className="mb-3 text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">
          Set up this tablet
        </h1>
        <p className="mb-6 text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
          {managedCheck === 'checking'
            ? 'Setting up this tablet...'
            : 'Display the tablet enrollment QR code from Cockpit on another screen, then scan it here.'}
        </p>
        {nativeEnrollmentAvailable && managedCheck !== 'checking' ? (
          <button
            type="button"
            onClick={() => void scanEnrollmentCode()}
            disabled={scanning}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-candy-pink px-4 py-3 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 dark:bg-candy-pink-dark dark:text-candy-dark-bg"
          >
            <QrCode aria-hidden size={18} />
            {scanning ? 'Scanning...' : 'Scan enrollment QR'}
          </button>
        ) : (
          !nativeEnrollmentAvailable ? (
            <p className="text-sm text-red-700 dark:text-red-300">
              Tablet enrollment is available only in the Sweet Spot Android app.
            </p>
          ) : null
        )}
        {error ? <p className="mt-4 text-sm text-red-700 dark:text-red-300">{error}</p> : null}
        {managedCheck === 'failed' ? (
          <button
            type="button"
            onClick={() => void checkManagedEnrollment()}
            className="mt-3 inline-flex items-center justify-center gap-2 text-sm font-medium text-candy-pink hover:underline dark:text-candy-pink-dark"
          >
            <RefreshCw aria-hidden size={16} />
            Retry automatic setup
          </button>
        ) : null}
      </section>
    </main>
  );
}
